import { useState } from 'react';

export default function PolicyContent() {
  const [activeSection, setActiveSection] = useState('collection');

  const sections = [
    { id: 'collection', label: 'Information Collection' },
    { id: 'usage', label: 'Data Usage' },
    { id: 'third-parties', label: 'Third Parties' },
    { id: 'security', label: 'Security Measures' },
    { id: 'rights', label: 'Your Rights' },
    { id: 'contact', label: 'Contact Us' },
  ];

  const content: Record<string, { title: string; paragraphs: string[] }> = {
    collection: {
      title: 'Information We Collect',
      paragraphs: [
        'At Next Phase Logistics LLC, we collect information necessary to provide efficient logistics services. This includes personal identification information such as name, email address, phone number, and business address when you request quotes or services.',
        'We also collect shipment-related data including pickup and delivery addresses, package dimensions, weight, contents description, and tracking information. This data is essential for processing and fulfilling your logistics requests.',
        'Technical information such as IP addresses, browser type, device information, and website usage patterns may be collected through cookies and similar technologies to improve our website functionality and user experience.',
      ],
    },
    usage: {
      title: 'How We Use Your Data',
      paragraphs: [
        'Your information is primarily used to process and fulfill logistics services, including coordinating pickups, deliveries, and providing real-time tracking updates. We use contact information to communicate with you about your shipments and service inquiries.',
        'We may use your data to improve our services, analyze trends, and develop new features. This helps us optimize routes, enhance delivery efficiency, and provide better customer experiences.',
        'With your consent, we may send promotional materials, newsletters, and service updates. You can opt out of marketing communications at any time while still receiving essential service-related notifications.',
      ],
    },
    'third-parties': {
      title: 'Data Sharing with Third Parties',
      paragraphs: [
        'We share necessary information with our network of carriers, drivers, and logistics partners to fulfill delivery services. These partners are contractually obligated to protect your information and use it solely for service delivery.',
        'Payment processors receive transaction information to process payments securely. We do not store complete credit card information on our servers.',
        'We may disclose information when required by law, to protect our rights, prevent fraud, or ensure safety. This includes cooperation with law enforcement and regulatory authorities when legally obligated.',
      ],
    },
    security: {
      title: 'Security Measures',
      paragraphs: [
        'We implement industry-standard security measures including SSL encryption for data transmission, secure servers with firewall protection, and regular security audits to protect your information from unauthorized access.',
        'Access to personal data is restricted to authorized employees and partners who need the information to perform their duties. All personnel are trained on data protection practices and confidentiality requirements.',
        'We maintain backup systems and disaster recovery procedures to ensure data integrity and availability. Regular monitoring helps us detect and respond to potential security threats promptly.',
      ],
    },
    rights: {
      title: 'Your Privacy Rights',
      paragraphs: [
        'You have the right to access, correct, or delete your personal information at any time. Contact us to request a copy of the data we hold about you or to update inaccurate information.',
        'You can opt out of marketing communications while continuing to receive essential service notifications. You also have the right to restrict certain data processing activities.',
        'If you believe your privacy rights have been violated, you have the right to file a complaint with relevant data protection authorities. We are committed to resolving any concerns promptly and transparently.',
      ],
    },
    contact: {
      title: 'Contact Information',
      paragraphs: [
        'For privacy-related inquiries, data access requests, or to exercise your rights, please contact us at:',
        'Email: mark@aikinslogistics.com',
        'Phone: +1 (317) 397-2529',
        'Address: 3505 Nolen Dr, Indianapolis, IN 46234, United States',
        'We will respond to all privacy inquiries within 30 days. For urgent matters, please call our customer support line during business hours.',
      ],
    },
  };

  return (
    <section className="py-20 bg-[#F4E8D8]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
          <div className="lg:col-span-1">
            <div className="bg-[#F4E8D8] rounded-2xl p-6 sticky top-24">
              <nav className="space-y-2">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full text-left px-4 py-3 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      activeSection === section.id
                        ? 'bg-[#E67E50] text-white'
                        : 'text-gray-700 hover:bg-white/50'
                    }`}
                  >
                    {section.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl shadow-sm p-12">
              <h2 className="text-4xl font-serif font-bold text-gray-900 mb-8">
                {content[activeSection].title}
              </h2>
              <div className="space-y-6">
                {content[activeSection].paragraphs.map((paragraph, index) => (
                  <p key={index} className="text-gray-600 leading-relaxed">
                    {paragraph}
                  </p>
                ))}
              </div>

              {activeSection === 'usage' && (
                <div className="mt-8 p-6 bg-[#F4E8D8] rounded-xl">
                  <h3 className="text-xl font-serif font-bold text-gray-900 mb-4">
                    Data Usage Summary
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#E67E50] mt-2 flex-shrink-0"></div>
                      <span className="text-sm text-gray-700">Service delivery and logistics coordination</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#E67E50] mt-2 flex-shrink-0"></div>
                      <span className="text-sm text-gray-700">Customer communication and support</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#E67E50] mt-2 flex-shrink-0"></div>
                      <span className="text-sm text-gray-700">Service improvement and analytics</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#E67E50] mt-2 flex-shrink-0"></div>
                      <span className="text-sm text-gray-700">Marketing communications (with consent)</span>
                    </li>
                  </ul>
                </div>
              )}

              {activeSection === 'security' && (
                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-6 border-2 border-[#E67E50] rounded-xl">
                    <i className="ri-shield-check-line text-3xl text-[#F5A962] mb-3"></i>
                    <h4 className="font-semibold text-gray-900 mb-2">SSL Encryption</h4>
                    <p className="text-sm text-gray-600">All data transmissions are encrypted</p>
                  </div>
                  <div className="p-6 border-2 border-[#E67E50] rounded-xl">
                    <i className="ri-lock-line text-3xl text-[#F5A962] mb-3"></i>
                    <h4 className="font-semibold text-gray-900 mb-2">Access Control</h4>
                    <p className="text-sm text-gray-600">Restricted to authorized personnel only</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
